import { CheckCircle, ChevronLeft, ChevronRight, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { RecentLoginsProps } from "./interfaces/interface";
import { Badge } from "../../ui/badge";
import { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Skeleton } from "../interfaces/CommonInterfaces";

export default function RecentLoginDetails({ fromdetails = false, recentLogins = [], loading }: RecentLoginsProps) {
  const [currentLoginPage, setCurrentLoginPage] = useState(1);

  const itemsPerPage = 7;
  const totalLoginPages = Math.ceil(recentLogins.length / itemsPerPage);
  const loginStartIndex = (currentLoginPage - 1) * itemsPerPage;
  const paginatedLogins = recentLogins.slice(loginStartIndex, loginStartIndex + itemsPerPage);

  useEffect(() => {
    setCurrentLoginPage(1);
  }, [recentLogins]);

  useEffect(() => {
    if (totalLoginPages > 0 && currentLoginPage > totalLoginPages) {
      setCurrentLoginPage(totalLoginPages);
    }
  }, [currentLoginPage, totalLoginPages]);

  const getVisiblePages = () => {
    if (totalLoginPages <= 30) {
      return Array.from({ length: totalLoginPages }, (_, i) => i + 1);
    }

    const visible: (number | string)[] = [];

    if (currentLoginPage <= 10) {
      visible.push(...Array.from({ length: 10 }, (_, i) => i + 1));
      visible.push("...");
      visible.push(totalLoginPages);
    } else if (currentLoginPage >= totalLoginPages - 9) {
      visible.push(1);
      visible.push("...");
      visible.push(
        ...Array.from({ length: 10 }, (_, i) => totalLoginPages - 9 + i)
      );
    } else {
      visible.push(1);
      visible.push("...");
      visible.push(currentLoginPage - 1, currentLoginPage, currentLoginPage + 1);
      visible.push("...");
      visible.push(totalLoginPages);
    }

    return visible;
  };


  const extractVal = (str: string) => {
    return str.includes("logged off") ? "logoff" : "login";
  };

  return (
    <Card className="flex flex-col flex-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Recent Logins</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col flex-1">
        <div className="flex-1 pr-1 space-y-2 overflow-y-auto">
          {loading
            ? // ✅ Skeleton placeholders while loading
            [...Array(7)].map((_, i) => (
              <div key={i} className="p-2 border rounded-md bg-background/50">
                <div className="flex items-center justify-between mb-1">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="w-16 h-4 rounded-full" />
                </div>
                <Skeleton className="w-full h-3 mb-1" />
                <Skeleton className="w-32 h-3" />
              </div>
            ))
            : paginatedLogins.length > 0 ?
              paginatedLogins.map((login) => (
                <div
                  key={login.id}
                  className="p-2 border rounded-md bg-background/50 hover-elevate"
                  data-testid={`login-${login.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium text-foreground">{login.computerName}</div>
                    <Badge
                      variant={extractVal(login.message) === "logoff" ? "destructive" : "default"}
                      className="flex items-center text-xs"
                    >
                      {extractVal(login.message) === "logoff" ? (
                        <XCircle className="w-3 h-3 mr-1" />
                      ) : (
                        <CheckCircle className="w-3 h-3 mr-1" />
                      )}
                      {extractVal(login.message)}
                    </Badge>
                  </div>
                  <div className="text-xs break-all text-muted-foreground">
                    {login.message}
                  </div>
                  <div className="text-xs text-muted-foreground">{login.updatedAt}</div>
                </div>
              )) : <div className="py-6 text-sm text-center text-muted-foreground">
                No Recent login found.
              </div>}
        </div>

        {!loading && recentLogins.length > itemsPerPage && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentLoginPage((prev) => Math.max(1, prev - 1))}
              disabled={currentLoginPage === 1}
              className="p-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            {getVisiblePages().map((page, idx) =>
              page === "..." ? (
                <span key={`dots-${idx}`} className="px-2 text-xs text-muted-foreground">
                  ...
                </span>
              ) : (
                <Button
                  key={page}
                  variant={currentLoginPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentLoginPage(Number(page))}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              )
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentLoginPage((prev) => Math.min(totalLoginPages, prev + 1))}
              disabled={currentLoginPage === totalLoginPages}
              className="p-2"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
